import axios, { AxiosRequestConfig, isCancel } from 'axios';
import { useAuthStore } from '../stores/auth';
import { message } from 'antd';
import i18n from 'i18next';

const request = axios.create({
  baseURL: '/api/user',
  timeout: 30000,
});

const errorCodeToI18n: Record<number, string> = {
  400: 'error.validation',
  401: 'error.invalid_credentials',
  403: 'error.account_disabled',
  404: 'error.not_found',
  409: 'error.conflict',
  500: 'error.system',
};

const getTranslatedError = (code: number, defaultMessage: string): string => {
  const i18nKey = errorCodeToI18n[code];
  if (i18nKey) {
    return i18n.t(i18nKey);
  }
  return defaultMessage;
};

request.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

request.interceptors.response.use(
  (response) => {
    const data = response.data;
    if (data && data.success === false) {
      if (data.code === 401) {
        useAuthStore.getState().clearAuth();
        window.location.href = '/login';
        return Promise.reject(new Error(data.message || 'Session expired'));
      }
      const err = new Error(data.message || 'Request failed');
      (err as any).response = { data };
      return Promise.reject(err);
    }
    return data;
  },
  (error: any) => {
    if (isCancel(error)) {
      return Promise.reject(error);
    }

    const businessCode = error.response?.data?.code;
    if (error.response?.status === 401 || businessCode === 401) {
      useAuthStore.getState().clearAuth();
      window.location.href = '/login';
      return Promise.reject(error);
    }

    const msg = error.response?.data?.message || error.message;
    const code = error.response?.data?.code || error.response?.status || 500;
    const translatedMsg = getTranslatedError(code, msg);

    message.error(translatedMsg);
    return Promise.reject(error);
  }
);

export default request;

export interface ApiResponse<T> {
  code: number;
  data: T;
  message: string;
  timestamp?: number;
  success?: boolean;
}

export const api = {
  get: <T = any>(url: string, config?: AxiosRequestConfig) =>
    request.get<ApiResponse<T>>(url, config).then(res => res.data as T),

  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    request.post<ApiResponse<T>>(url, data, config).then(res => res.data as T),

  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    request.put<ApiResponse<T>>(url, data, config).then(res => res.data as T),

  delete: <T = any>(url: string, config?: AxiosRequestConfig) =>
    request.delete<ApiResponse<T>>(url, config).then(res => res.data as T),
};
